import { Button } from '@/components/design-system/Button';
import InputWithIcon from '@/components/ui/InputWithIcon';
import { FormInput } from '@/features/form/inputs/FormInput';
import { FormPassword } from '@/features/form/inputs/FormPassword';
import { cn } from '@/utils/cn';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircleCheck, Lock, Mail, User } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const acceptInvitationSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string()
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword']
  });

type AcceptInvitationFormValues = z.infer<typeof acceptInvitationSchema>;

interface AcceptInvitationFormProps {
  onSubmit: (data: Omit<AcceptInvitationFormValues, 'confirmPassword'>) => Promise<boolean>;
  loading?: boolean;
  email?: string | null;
}

export const AcceptInvitationForm = ({ onSubmit, loading, email }: AcceptInvitationFormProps) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { isValid }
  } = useForm<AcceptInvitationFormValues>({
    resolver: zodResolver(acceptInvitationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: ''
    },
    mode: 'onTouched'
  });

  const handleFormSubmit = async (data: AcceptInvitationFormValues) => {
    const { confirmPassword: _, ...submitData } = data;
    await onSubmit(submitData);
  };

  const isDisabled = loading === true || !isValid;
  const isLoading = loading === true;

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-4">
      {email !== null && email !== undefined && (
        <InputWithIcon
          type="email"
          value={email}
          readOnly
          tabIndex={-1}
          status="success"
          leftIcon={Mail}
          rightIcon={CircleCheck}
          className="pointer-events-none [&_input]:bg-neutral-100 [&_input]:text-neutral-400"
          aria-label={t('acceptInvitation.emailLabel')}
        />
      )}

      <FormInput
        control={control}
        name="firstName"
        leftIcon={User}
        placeholder="acceptInvitation.firstNamePlaceholder"
        autoComplete="off"
        disabled={isLoading}
      />

      <FormInput
        control={control}
        name="lastName"
        leftIcon={User}
        placeholder="acceptInvitation.lastNamePlaceholder"
        autoComplete="off"
        disabled={isLoading}
      />

      <FormPassword
        control={control}
        name="password"
        leftIcon={Lock}
        placeholder="acceptInvitation.passwordPlaceholder"
        autoComplete="new-password"
      />

      <FormPassword
        control={control}
        name="confirmPassword"
        leftIcon={Lock}
        placeholder="acceptInvitation.confirmPasswordPlaceholder"
        autoComplete="new-password"
      />

      <Button
        type="submit"
        loading={loading}
        disabled={isDisabled}
        className={cn(
          'h-10 w-81 border text-base transition-all duration-200',
          isLoading && 'cursor-wait opacity-60',
          !isLoading && isValid
            ? 'bg-primary border-primary text-typography hover:bg-primary/90 hover:text-typography/90'
            : 'bg-secondary-900/4 border-border-100 text-secondary-900/25 cursor-not-allowed'
        )}
      >
        {t('acceptInvitation.submit')}
      </Button>
    </form>
  );
};
